import { useState, useEffect } from "react";
import { X, Phone, Loader2, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CallbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}


const CallbackModal = ({ isOpen, onClose }: CallbackModalProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formStartTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setName("");
      setPhone("");
      setWebsiteUrl("");
      setRemarks("");
      setIsSuccess(false);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;

    if (!name.trim() || !phone.trim()) {
      toast({
        title: "Vul alle velden in",
        description: "Naam en telefoonnummer zijn verplicht.",
        variant: "destructive",
      });
      return;
    }

    const phoneRegex = /^[\d\s\-+()]{8,}$/;
    if (!phoneRegex.test(phone)) {
      toast({
        title: "Ongeldig telefoonnummer",
        description: "Vul een geldig telefoonnummer in.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const websitePart = websiteUrl.trim() ? `\nWebsite: ${websiteUrl.trim()}` : "";
      const remarksPart = remarks.trim() ? `\nOpmerking: ${remarks.trim()}` : "";

      const [whatsappResult, emailResult] = await Promise.allSettled([
        supabase.functions.invoke("notify-whatsapp", {
          body: {
            visitorName: name.trim(),
            message: `Terugbelverzoek: Bel mij terug op ${phone.trim()}${websitePart}${remarksPart}`,
            _formStartTime: formStartTime,
            website: honeypot,
          },
        }),
        supabase.functions.invoke("send-quote-email", {
          body: {
            name: name.trim(),
            email: "niet-opgegeven@placeholder.nl",
            phone: phone.trim(),
            service: "Terugbelverzoek",
            message: `Terugbelverzoek via de website.\n\nTelefoonnummer: ${phone.trim()}${websitePart}${remarksPart}`,
          },
        }),
      ]);

      const whatsappOk = whatsappResult.status === "fulfilled" && !whatsappResult.value.error;
      const emailOk = emailResult.status === "fulfilled" && !emailResult.value.error;
      if (!whatsappOk && !emailOk) throw new Error("Beide notificaties mislukt");
      window.location.href = "/gelukt?type=terugbel";
    } catch (error) {
      console.error("Error sending callback request:", error);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of bel ons direct.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white w-full md:max-w-sm md:rounded-2xl rounded-t-2xl shadow-2xl animate-scale-in max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-border/50">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
              <Phone className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground leading-tight"><span className="accent-serif">Terugbelverzoek</span></h3>
              <p className="text-xs text-muted-foreground">
                Ik bel je zo snel mogelijk terug
              </p>
            </div>
          </div>
        </div>

        {isSuccess ? (
          <div className="text-center py-10 px-5">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-foreground mb-1"><span className="accent-serif">Bedankt!</span></h3>
            <p className="text-sm text-muted-foreground">
              Ik bel je zo snel mogelijk terug.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3.5">
            <input
              type="text"
              name="website_hp"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Name & Phone side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Naam *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Je naam"
                  maxLength={100}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Telefoon *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="06-12345678"
                  maxLength={20}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                  required
                />
              </div>
            </div>

            {/* Website URL */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Huidige website
              </label>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://www.jouwwebsite.nl"
                maxLength={200}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
              />
            </div>

            {/* Opmerkingen */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Opmerkingen
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Waar kunnen we je mee helpen?"
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-none"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                "w-full py-3 rounded-xl font-semibold text-white text-sm",
                "bg-accent hover:bg-accent/90",
                "focus:outline-none focus:ring-4 focus:ring-accent/30",
                "transition-all duration-200 mt-1",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "flex items-center justify-center gap-2"
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verzenden...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  Bel mij terug
                </>
              )}
            </button>

            <p className="text-[11px] text-muted-foreground text-center pb-1">
              Door dit formulier in te vullen ga je akkoord met ons{" "}
              <a href="/privacybeleid" className="underline hover:text-foreground">
                privacybeleid
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default CallbackModal;
